<template>
  <HrPageLayout
    title="Employee Records"
    subtitle="Track staff files: Present Active, Missing Active, Stoppage, Transferred out, Attrition"
    icon="📁"
    :breadcrumb-items="[
      { label: 'Dashboard', route: '/dashboard' },
      { label: 'Employee Records Management' },
      { label: 'Employee Records' }
    ]"
  >
    <template #header-actions>
      <button
        v-if="canReport"
        type="button"
        class="hr-btn hr-btn-outline-success"
        :disabled="!records.length || generatingReport"
        @click="generateReportPdf"
      >
        <span v-if="generatingReport" class="hr-spinner small"></span>
        <i v-else class="bx bx-file"></i>
        Generate Report (PDF)
      </button>
      <button v-if="canAdd" class="hr-btn hr-btn-primary" @click="openAddModal">
        <i class="bx bx-plus"></i> Add File Record
      </button>
    </template>

    <div class="file-registry-content">
      <div v-if="accessDenied" class="alert alert-danger">
        You do not have permission to view Employee Records. Requires <strong>EMPLOYEE RECORDS</strong> (or VIEW/ADD/EDIT/DOWNLOAD rights).
      </div>

      <!-- Stats -->
      <div class="hr-metrics-grid hr-mb-4">
        <div class="hr-metric-card gradient-1">
          <div class="hr-metric-icon"><i class="bx bx-folder"></i></div>
          <p class="hr-metric-label">Total Records</p>
          <h2 class="hr-metric-value">{{ stats.total }}</h2>
        </div>
        <div class="hr-metric-card gradient-2">
          <div class="hr-metric-icon"><i class="bx bx-check-circle"></i></div>
          <p class="hr-metric-label">Present Active</p>
          <h2 class="hr-metric-value">{{ stats.present_active }}</h2>
        </div>
        <div class="hr-metric-card gradient-3">
          <div class="hr-metric-icon"><i class="bx bx-error-circle"></i></div>
          <p class="hr-metric-label">Missing Active</p>
          <h2 class="hr-metric-value">{{ stats.missing_active }}</h2>
        </div>
        <div class="hr-metric-card gradient-4">
          <div class="hr-metric-icon"><i class="bx bx-pause-circle"></i></div>
          <p class="hr-metric-label">Stoppage</p>
          <h2 class="hr-metric-value">{{ stats.stoppage }}</h2>
        </div>
        <div class="hr-metric-card gradient-2">
          <div class="hr-metric-icon"><i class="bx bx-transfer"></i></div>
          <p class="hr-metric-label">Transferred out</p>
          <h2 class="hr-metric-value">{{ stats.transferred_out }}</h2>
        </div>
        <div class="hr-metric-card gradient-3">
          <div class="hr-metric-icon"><i class="bx bx-user-x"></i></div>
          <p class="hr-metric-label">Attrition</p>
          <h2 class="hr-metric-value">{{ stats.attrition }}</h2>
        </div>
      </div>

      <!-- Filters -->
      <div class="hr-chart-card hr-mb-4">
        <div class="hr-chart-body hr-py-3">
          <div class="hr-filters-row">
            <div class="hr-search-wrap">
              <input
                v-model="search"
                type="text"
                class="hr-input"
                placeholder="Search by record number or employee name..."
                spellcheck="false"
                @input="debouncedLoad"
              >
              <i class="bx bx-search-alt search-icon"></i>
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Category</label>
              <select v-model="filterStatus" @change="loadRecords" class="hr-select" style="min-width: 200px;">
                <option value="">All categories</option>
                <option v-for="opt in statusOptions" :key="'fs-' + opt.value" :value="opt.value">{{ opt.label }}</option>
              </select>
              <select v-model="filterDepartmentId" @change="loadRecords" class="hr-select" style="min-width: 220px;">
                <option value="">All departments</option>
                <option v-for="d in departments" :key="'fd-' + d.id" :value="String(d.id)">{{ d.departmentName }}</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="hr-chart-card">
        <div class="hr-chart-body">
          <div class="hr-table-wrapper" style="overflow-x: auto;">
            <table class="hr-table hr-table-hover hr-table-striped">
              <thead>
                <tr>
                  <th>Record Number</th>
                  <th>Employee</th>
                  <th>Emp No</th>
                  <th>Department</th>
                  <th>Employment Date</th>
                  <th>Category</th>
                  <th>Attrition nature</th>
                  <th>Remarks</th>
                  <th>Date document uploaded</th>
                  <th>Created by / when</th>
                  <th>Last edit by / when</th>
                  <th>Document upload by / when</th>
                  <th>File(s)</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loading">
                  <td colspan="14" class="hr-text-center hr-py-4"><div class="hr-spinner"></div></td>
                </tr>
                <tr v-else-if="filteredRecords.length === 0">
                  <td colspan="14" class="hr-text-center hr-py-4 hr-text-muted">No records found</td>
                </tr>
                <tr v-for="row in filteredRecords" :key="row.id">
                  <td><strong>{{ row.record_number }}</strong></td>
                  <td>{{ row.employee_name || '—' }}</td>
                  <td><span class="hr-badge hr-badge-secondary">{{ row.emp_no || row.empNo || '—' }}</span></td>
                  <td>{{ row.department_name || row.departmentName || '—' }}</td>
                  <td>{{ formatDate(row.employment_date || row.employmentDate) }}</td>
                  <td>
                    <span :class="'hr-badge ' + statusBadgeClass(row.status)">{{ formatStatus(row.status) }}</span>
                  </td>
                  <td>{{ formatAttritionNature(row) }}</td>
                  <td class="hr-text-muted hr-small">{{ truncate(row.notes, 50) }}</td>
                  <td>{{ formatDate(row.status_date) }}</td>
                  <td class="hr-small">
                    <div>{{ row.created_by || '—' }}</div>
                    <div class="hr-text-muted">{{ formatDateTime(row.created_at) }}</div>
                  </td>
                  <td class="hr-small">
                    <div>{{ row.updated_by || '—' }}</div>
                    <div class="hr-text-muted">{{ formatDateTime(row.updated_at) }}</div>
                  </td>
                  <td class="hr-small">
                    <div>{{ row.file_uploaded_by || '—' }}</div>
                    <div class="hr-text-muted">{{ formatDateTime(row.file_uploaded_at) }}</div>
                  </td>
                  <td>
                    <div v-if="getFilesListFromRow(row).length" class="registry-file-list">
                      <div v-for="(file, idx) in getFilesListFromRow(row)" :key="idx" class="registry-file-item">
                        <div class="registry-file-info">
                          <i class="bx bx-file registry-file-icon"></i>
                          <span class="registry-file-name" :title="getFileName(file)">{{ getFileName(file) }}</span>
                        </div>
                        <div class="hr-btn-group">
                          <button
                            v-if="canDownload"
                            type="button"
                            class="hr-btn hr-btn-secondary btn-compact"
                            @click="openRegistryFileByIndex(idx, row.id)"
                            title="View File"
                          >
                            <i class="bx bx-show"></i> View
                          </button>
                          <button
                            v-if="canDownload"
                            type="button"
                            class="hr-btn hr-btn-info btn-compact"
                            @click="downloadRegistryFileByIndex(idx, file, row)"
                            title="Download File"
                          >
                            <i class="bx bx-download"></i> Download
                          </button>
                        </div>
                      </div>
                    </div>
                    <span v-else class="hr-text-muted hr-small">—</span>
                  </td>
                  <td>
                    <div class="hr-btn-group">
                      <button
                        v-if="canEdit"
                        type="button"
                        class="hr-btn hr-btn-sm hr-btn-primary"
                        @click="editRecord(row)"
                        title="Edit"
                      >
                        <i class="bx bx-edit"></i> Edit
                      </button>
                      <span v-else class="hr-text-muted hr-small">—</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Add/Edit Modal -->
      <ModalWrapper ref="formModal" :title="editingId ? 'Edit File Record' : 'Add File Record'" size="large">
        <template #body>
          <form @submit.prevent="saveRecord" class="hr-form-grid">
            <div class="hr-filter-group span-full">
              <label class="hr-form-label">Record Number <span class="hr-text-danger">*</span></label>
              <input
                v-model="form.record_number"
                type="text"
                class="hr-input"
                placeholder="REG/HRIS/024/0001"
                required
                :disabled="!!editingId"
              >
              <small v-if="!editingId" class="hr-text-muted">
                Format: <strong>REG/HRIS/024/0001</strong>
                <span v-if="suggestedRecordNumber"> — suggested next: <button type="button" class="hr-btn hr-btn-link hr-btn-sm p-0" @click="form.record_number = suggestedRecordNumber">{{ suggestedRecordNumber }}</button></span>
              </small>
              <small v-if="editingId" class="hr-text-muted">Record number cannot be changed after creation.</small>
            </div>
            <div class="hr-filter-group span-full">
              <label class="hr-form-label">Employee <span class="hr-text-danger">*</span></label>
              <input
                v-model.trim="employeePickerSearch"
                type="text"
                class="hr-input hr-mb-2"
                placeholder="Search by employee number, national ID, or name..."
                :disabled="!!editingId"
              >
              <select v-model="form.employee_id" class="hr-select" required :disabled="!!editingId || loadingEmployees">
                <option value="">{{ loadingEmployees ? 'Loading employees…' : 'Select employee' }}</option>
                <option v-for="emp in filteredEmployeesForPicker" :key="'emp-' + emp.id" :value="String(emp.id)">
                  {{ empLabel(emp) }}
                </option>
              </select>
              <small v-if="!loadingEmployees && !editingId && !filteredEmployeesForPicker.length" class="text-danger hr-small">
                No employees found. Type employee number / national ID / name to search, or check you have access to the staff list.
              </small>
              <small v-else-if="!editingId" class="hr-text-muted hr-small">
                Showing {{ filteredEmployeesForPicker.length }} employee(s). Type to search beyond the first page.
              </small>
            </div>

            <div v-if="selectedEmployeeDetails" class="hr-filter-group span-half">
              <label class="hr-form-label">Department</label>
              <input
                :value="selectedEmployeeDetails.departmentName || selectedEmployeeDetails.department_name || '—'"
                type="text"
                class="hr-input"
                readonly
                disabled
              >
            </div>
            <div v-if="selectedEmployeeDetails" class="hr-filter-group span-half">
              <label class="hr-form-label">Employment Date</label>
              <input
                :value="formatDate(selectedEmployeeDetails.employmentDate || selectedEmployeeDetails.employment_date || selectedEmployeeDetails.hireDate)"
                type="text"
                class="hr-input"
                readonly
                disabled
              >
            </div>

            <div class="hr-filter-group span-full">
              <label class="hr-form-label">Record category <span class="hr-text-danger">*</span></label>
              <select v-model="form.status" class="hr-select" required @change="onStatusChange">
                <option v-for="opt in statusOptions" :key="'st-' + opt.value" :value="opt.value">{{ opt.label }}</option>
              </select>
            </div>

            <div v-if="form.status === 'attrition'" class="hr-filter-group span-full">
              <label class="hr-form-label">Nature of attrition <span class="hr-text-danger">*</span></label>
              <select v-model="form.attrition_nature" class="hr-select" required>
                <option value="">Select nature…</option>
                <option value="deceased">Deceased</option>
                <option value="retired">Retired</option>
                <option value="dismissed">Dismissed</option>
                <option value="resigned">Resigned</option>
              </select>
            </div>

            <div v-if="form.status === 'transferred_out'" class="hr-filter-group span-full">
              <label class="hr-form-label">Transferred To</label>
              <input v-model="form.transferred_to" type="text" class="hr-input" placeholder="Where was the file transferred?">
            </div>

            <div class="hr-filter-group span-half">
              <label class="hr-form-label">Upload date</label>
              <input v-model="form.status_date" type="date" class="hr-input">
            </div>

            <div class="hr-filter-group span-full">
              <label class="hr-form-label">Remarks</label>
              <textarea v-model="form.notes" class="hr-textarea" rows="3" placeholder="Optional remarks"></textarea>
            </div>

            <div class="hr-filter-group span-full">
              <label class="hr-form-label">Employee files (PDF, ZIP, Word, Images — up to {{ maxUploadMb }}MB each)</label>
              <input
                ref="pdfFileInput"
                type="file"
                class="hr-input"
                accept=".pdf,.zip,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp,application/pdf,application/zip,image/*"
                multiple
                @change="onPdfFilesChange"
              >
              <small class="hr-text-muted hr-d-block hr-mt-1">
                Multiple files allowed (up to {{ maxUploadMb }}MB each). Large files are saved one at a time with automatic retry if the connection drops.
              </small>
              <div v-if="uploadSizeError" class="text-danger hr-small hr-mt-1">{{ uploadSizeError }}</div>
              <div v-if="uploadProgressText" class="hr-small text-primary hr-mt-1">{{ uploadProgressText }}</div>
              <div v-if="uploadProgress > 0 && uploadProgress < 100" class="progress hr-mt-2" style="height: 8px;">
                <div class="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" :style="{ width: uploadProgress + '%' }"></div>
              </div>
              <div v-if="pdfFiles.length" class="hr-mt-2 hr-flex-col" style="gap: 4px;">
                <div v-for="(f, i) in pdfFiles" :key="i" class="hr-flex-between">
                  <span class="hr-badge hr-badge-success">
                    <i class="bx bx-file"></i> {{ f.name }} ({{ formatFileSize(f.size) }})
                  </span>
                  <button type="button" class="hr-btn hr-btn-link hr-btn-sm hr-text-danger" @click.prevent="removeSelectedFile(i)">
                    <i class="bx bx-x"></i> Remove
                  </button>
                </div>
              </div>
              <div v-if="editingId && getFilesList().length" class="hr-mt-3">
                <label class="hr-form-label hr-small">Currently Uploaded Files:</label>
                <div class="modal-file-list" style="margin-top: 6px;">
                  <div v-for="(file, idx) in getFilesList()" :key="idx" class="modal-file-item">
                    <span class="hr-small text-truncate" style="max-width: 250px; font-weight: 500;" :title="getFileName(file)">
                      <i class="bx bx-file" style="color: #059669;"></i> {{ getFileName(file) }}
                    </span>
                    <div class="hr-btn-group">
                      <button v-if="canDownload" type="button" class="hr-btn hr-btn-sm hr-btn-secondary" @click.prevent="openRegistryFileByIndex(idx)">
                        <i class="bx bx-show"></i> View
                      </button>
                      <button v-if="canDownload" type="button" class="hr-btn hr-btn-sm hr-btn-info" @click.prevent="downloadRegistryFileByIndex(idx, file)">
                        <i class="bx bx-download"></i> Download
                      </button>
                      <button v-if="canDeleteFile" type="button" class="hr-btn hr-btn-sm hr-btn-danger" @click.prevent="deleteRegistryFileByIndex(idx)">
                        <i class="bx bx-trash"></i> Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="editingId" class="hr-filter-group span-full audit-box">
              <div class="hr-small"><strong>Created:</strong> {{ formAudit.created_by || '—' }} · {{ formatDateTime(formAudit.created_at) }}</div>
              <div class="hr-small"><strong>Last edit:</strong> {{ formAudit.updated_by || '—' }} · {{ formatDateTime(formAudit.updated_at) }}</div>
              <div class="hr-small"><strong>Document upload:</strong> {{ formAudit.file_uploaded_by || '—' }} · {{ formatDateTime(formAudit.file_uploaded_at) }}</div>
            </div>
          </form>
        </template>
        <template #footer>
          <button type="button" class="hr-btn hr-btn-secondary" @click="$refs.formModal?.close()">Cancel</button>
          <button type="button" class="hr-btn hr-btn-primary" @click="saveRecord" :disabled="saving || !!uploadSizeError">
            <span v-if="saving" class="hr-spinner small"></span>
            <i v-else class="bx bx-check"></i> {{ editingId ? 'Update' : 'Save' }}
          </button>
        </template>
      </ModalWrapper>
    </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import ModalWrapper from "@/components/hrActions/ModalWrapper.vue";
import { execute, executeUpload, executeDownload, authUrl, getRight } from "@/api";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import {
  loadCountyLogoDataUrl,
  addCountyLetterhead
} from "@/utils/payrollReconPdf";

const STATUS_OPTIONS = [
  { value: "present_active", label: "Present Active" },
  { value: "missing_active", label: "Missing Active files" },
  { value: "stoppage", label: "Stoppage" },
  { value: "transferred_out", label: "Transferred out" },
  { value: "attrition", label: "Attrition" }
];

function normalizeStatus(s) {
  const t = String(s || "").toLowerCase().trim();
  const map = {
    available: "present_active",
    missing: "missing_active",
    transferred: "transferred_out",
    "transferred out": "transferred_out",
    closed: "stoppage",
    exit: "stoppage",
    deceased: "attrition",
    retired: "attrition",
    dismissed: "attrition",
    resigned: "attrition",
    present_active: "present_active",
    missing_active: "missing_active",
    stoppage: "stoppage",
    transferred_out: "transferred_out",
    attrition: "attrition"
  };
  return map[t] || t || "present_active";
}

export default {
  name: "FileRegistry",
  components: { HrPageLayout, ModalWrapper },
  data() {
    return {
      records: [],
      employees: [],
      departments: [],
      loading: false,
      loadingEmployees: false,
      saving: false,
      generatingReport: false,
      accessDenied: false,
      search: "",
      filterStatus: "",
      filterDepartmentId: "",
      employeePickerSearch: "",
      editingId: null,
      serverNextRecordNumber: "",
      form: {
        record_number: "",
        employee_id: "",
        status: "present_active",
        attrition_nature: "",
        notes: "",
        transferred_to: "",
        status_date: new Date().toISOString().slice(0, 10)
      },
      formAudit: {},
      pdfFiles: [],
      existingPdfPath: null,
      uploadSizeError: "",
      maxUploadMb: 50,
      uploadProgress: 0,
      uploadProgressText: "",
      letterhead: null,
      capabilities: {
        canView: true,
        canAdd: true,
        canEdit: true,
        canDownload: true,
        canDeleteFile: true,
        canReport: true
      },
      debounceTimer: null,
      employeeSearchTimer: null,
      statusOptions: STATUS_OPTIONS
    };
  },
  computed: {
    canAdd() {
      return this.capabilities.canAdd || getRight([
        "EMPLOYEE RECORDS", "EMPLOYEE RECORDS ADD", "EMPLOYEE RECORDS MANAGE",
        "FILE REGISTRY", "FILE REGISTRY ADD", "FILE REGISTRY MANAGE"
      ]);
    },
    canEdit() {
      return this.capabilities.canEdit || getRight([
        "EMPLOYEE RECORDS", "EMPLOYEE RECORDS EDIT", "EMPLOYEE RECORDS MANAGE",
        "FILE REGISTRY", "FILE REGISTRY EDIT", "FILE REGISTRY MANAGE"
      ]);
    },
    canDownload() {
      return this.capabilities.canDownload || getRight([
        "EMPLOYEE RECORDS", "EMPLOYEE RECORDS DOWNLOAD", "EMPLOYEE RECORDS VIEW", "EMPLOYEE RECORDS MANAGE",
        "FILE REGISTRY", "FILE REGISTRY DOWNLOAD", "FILE REGISTRY VIEW", "FILE REGISTRY MANAGE"
      ]);
    },
    canDeleteFile() {
      return this.capabilities.canDeleteFile || getRight([
        "EMPLOYEE RECORDS", "EMPLOYEE RECORDS EDIT", "EMPLOYEE RECORDS ADD", "EMPLOYEE RECORDS MANAGE",
        "FILE REGISTRY", "FILE REGISTRY EDIT", "FILE REGISTRY ADD", "FILE REGISTRY MANAGE"
      ]);
    },
    canReport() {
      return this.capabilities.canReport || getRight([
        "EMPLOYEE RECORDS", "EMPLOYEE RECORDS VIEW", "EMPLOYEE RECORDS REPORT", "EMPLOYEE RECORDS MANAGE", "RECORDS DASHBOARD",
        "FILE REGISTRY", "FILE REGISTRY VIEW", "FILE REGISTRY REPORT", "FILE REGISTRY MANAGE"
      ]);
    },
    filteredEmployeesForPicker() {
      const list = Array.isArray(this.employees) ? this.employees : [];
      const q = (this.employeePickerSearch || "").trim().toLowerCase();
      if (!q) return list;
      return list.filter((emp) => this.buildEmployeeSearchText(emp).includes(q));
    },
    filteredRecords() {
      return this.records;
    },
    selectedEmployeeDetails() {
      if (!this.form.employee_id) return null;
      return this.employees.find((emp) => String(emp.id) === String(this.form.employee_id));
    },
    yearToken() {
      // e.g. 2024 → 024, 2026 → 026 (REG/HRIS/024/0001)
      return String(new Date().getFullYear()).slice(-3).padStart(3, "0");
    },
    suggestedRecordNumber() {
      // Prefer server-computed next number (full DB), fall back to client list
      if (this.serverNextRecordNumber) return this.serverNextRecordNumber;
      const token = this.yearToken;
      const prefix = `REG/HRIS/${token}/`;
      let maxSeq = 0;
      (this.records || []).forEach((r) => {
        const rn = String(r.record_number || "").trim().toUpperCase();
        const m = rn.match(new RegExp(`^REG\\/HRIS\\/${token}\\/(\\d{1,6})$`, "i"));
        if (m) maxSeq = Math.max(maxSeq, parseInt(m[1], 10) || 0);
      });
      const next = String(maxSeq + 1).padStart(4, "0");
      return `${prefix}${next}`;
    },
    stats() {
      const s = {
        total: 0,
        present_active: 0,
        missing_active: 0,
        stoppage: 0,
        transferred_out: 0,
        attrition: 0
      };
      this.records.forEach((r) => {
        s.total++;
        const st = normalizeStatus(r.status);
        if (s[st] != null) s[st]++;
      });
      return s;
    },
    reportSummary() {
      const attrition = { deceased: 0, retired: 0, dismissed: 0, resigned: 0, other: 0 };
      this.records.forEach((r) => {
        if (normalizeStatus(r.status) !== "attrition") return;
        let n = String(r.attrition_nature || "").toLowerCase();
        const legacy = String(r.status || "").toLowerCase();
        if (!n && ["deceased", "retired", "dismissed", "resigned"].includes(legacy)) n = legacy;
        if (attrition[n] != null) attrition[n]++;
        else attrition.other++;
      });
      return {
        total: this.stats.total,
        present_active: this.stats.present_active,
        missing_active: this.stats.missing_active,
        stoppage: this.stats.stoppage,
        transferred_out: this.stats.transferred_out,
        attrition: this.stats.attrition,
        attrition_breakdown: attrition
      };
    }
  },
  mounted() {
    this.loadRecords();
    this.loadDepartments();
    this.loadLetterhead();
  },
  watch: {
    employeePickerSearch() {
      this.debouncedEmployeeSearch();
    },
    employees() {
      this.autoSelectEmployeeFromSearch();
    }
  },
  methods: {
    empLabel(emp) {
      const name = [emp.firstName, emp.middleName || emp.middle_name, emp.lastName].filter(Boolean).join(" ");
      const no =
        emp.empNo || emp.emp_no || emp.employeeNo || emp.nationalId || emp.national_id || "";
      return `${name} (${no})`;
    },
    truncate(s, n) {
      const t = String(s || "");
      return t.length > n ? t.slice(0, n) + "…" : t;
    },
    debouncedLoad() {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.loadRecords(), 300);
    },
    debouncedEmployeeSearch() {
      if (this.employeeSearchTimer) clearTimeout(this.employeeSearchTimer);
      this.employeeSearchTimer = setTimeout(() => {
        this.loadEmployees(this.employeePickerSearch);
      }, 300);
    },
    async loadLetterhead() {
      try {
        const data = new FormData();
        data.append("function", "getCompanyInfo");
        const res = await execute(data, authUrl);
        if (res?.data?.success && res.data.data?.companyInfo) {
          const c = res.data.data.companyInfo;
          const abs = (u) => {
            if (!u) return "";
            if (/^https?:\/\//i.test(u)) return u;
            try {
              return new URL(u, authUrl).href;
            } catch (_) {
              return u;
            }
          };
          this.letterhead = {
            ...c,
            logoPrimaryUrl: abs(c.logoPrimaryUrl || c.logoPrimary),
            logoSecondaryUrl: abs(c.logoSecondaryUrl || c.logoSecondary)
          };
        }
      } catch (_) {
        this.letterhead = null;
      }
    },
    async loadRecords() {
      this.loading = true;
      this.accessDenied = false;
      try {
        const data = new FormData();
        data.append("function", "getStaffFileRegistry");
        data.append("keyword", this.search);
        if (this.filterStatus) data.append("status", this.filterStatus);
        if (this.filterDepartmentId) data.append("departmentId", this.filterDepartmentId);
        const res = await execute(data, authUrl);
        if (res?.data?.success === false) {
          const msg = res.data.message || "";
          if (/access denied/i.test(msg)) this.accessDenied = true;
          this.records = [];
          return;
        }
        if (res?.data?.success && Array.isArray(res.data.data?.records)) {
          this.records = res.data.data.records;
          if (res.data.data.nextRecordNumber) {
            this.serverNextRecordNumber = String(res.data.data.nextRecordNumber).trim();
          }
          const cap = res.data.data.capabilities;
          if (cap) {
            this.capabilities = { ...this.capabilities, ...cap };
            if (cap.maxUploadMb) this.maxUploadMb = Number(cap.maxUploadMb) || 50;
          }
        } else {
          this.records = res?.data?.records ?? [];
          if (res?.data?.nextRecordNumber) {
            this.serverNextRecordNumber = String(res.data.nextRecordNumber).trim();
          }
        }
      } catch (e) {
        console.error("File registry load error:", e);
        this.records = [];
      } finally {
        this.loading = false;
      }
    },
    async loadDepartments() {
      try {
        const data = new FormData();
        data.append("function", "getDepartments");
        const res = await execute(data, authUrl);
        this.departments = res?.data?.departments ?? res?.data?.data?.departments ?? [];
      } catch (e) {
        this.departments = [];
      }
    },
    async loadEmployees(keyword = "") {
      this.loadingEmployees = true;
      try {
        const data = new FormData();
        data.append("function", "getEmployees");
        data.append("scopeModule", "registry");
        data.append("page", "1");
        data.append("rows_per_page", "500");
        const searchText = String(keyword || "").trim();
        if (searchText) data.append("keyword", searchText);
        const res = await execute(data, authUrl);
        const body = res?.data || {};
        let list =
          body?.data?.employees ??
          body?.employees ??
          body?.data?.list ??
          body?.list ??
          null;
        if (!Array.isArray(list) && Array.isArray(body?.data)) list = body.data;
        if (!Array.isArray(list) && Array.isArray(body)) list = body;
        this.employees = Array.isArray(list) ? list : [];
      } catch (e) {
        console.error("loadEmployees", e);
        this.employees = [];
      } finally {
        this.loadingEmployees = false;
        this.autoSelectEmployeeFromSearch();
      }
    },
    getEmployeeSearchValue(emp, key) {
      if (key === "employeeNo") {
        return String(
          emp?.empNo || emp?.emp_no || emp?.employeeNo || emp?.employee_number || emp?.employeeNumber || emp?.personalNo || emp?.payrollNo || ""
        ).trim();
      }
      if (key === "nationalId") return String(emp?.nationalId || emp?.national_id || "").trim();
      if (key === "fullName") {
        return `${emp?.firstName || ""} ${emp?.middleName || emp?.middle_name || ""} ${emp?.lastName || ""}`
          .replace(/\s+/g, " ")
          .trim();
      }
      return "";
    },
    buildEmployeeSearchText(emp) {
      return [
        this.getEmployeeSearchValue(emp, "fullName"),
        this.getEmployeeSearchValue(emp, "employeeNo"),
        this.getEmployeeSearchValue(emp, "nationalId"),
        emp?.email,
        emp?.departmentName,
        emp?.department_name
      ]
        .map((v) => String(v || "").trim())
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
    },
    autoSelectEmployeeFromSearch() {
      if (this.editingId) return;
      const query = String(this.employeePickerSearch || "").trim();
      if (!query) return;
      const qLower = query.toLowerCase();
      const list = this.filteredEmployeesForPicker || [];
      if (!list.length) return;
      const exactMatches = list.filter((emp) => {
        const employeeNo = this.getEmployeeSearchValue(emp, "employeeNo").toLowerCase();
        const nationalId = this.getEmployeeSearchValue(emp, "nationalId").toLowerCase();
        return employeeNo === qLower || nationalId === qLower;
      });
      if (exactMatches.length === 1) this.form.employee_id = String(exactMatches[0].id);
      else if (list.length === 1) this.form.employee_id = String(list[0].id);
    },
    async openAddModal() {
      if (!this.canAdd) {
        window.alert("You do not have permission to add records.");
        return;
      }
      // Refresh list so suggested number matches the latest DB state
      await this.loadRecords();
      this.editingId = null;
      this.employeePickerSearch = "";
      this.pdfFiles = [];
      this.existingPdfPath = null;
      this.uploadSizeError = "";
      this.uploadProgress = 0;
      this.uploadProgressText = "";
      this.formAudit = {};
      this.form = {
        record_number: this.suggestedRecordNumber || this.serverNextRecordNumber || "",
        employee_id: "",
        status: "present_active",
        attrition_nature: "",
        notes: "",
        transferred_to: "",
        status_date: new Date().toISOString().slice(0, 10)
      };
      this.loadEmployees("");
      this.$refs.formModal?.open();
      this.$nextTick(() => this.resetPdfInput());
    },
    editRecord(row) {
      if (!this.canEdit) {
        window.alert("You do not have permission to edit records.");
        return;
      }
      this.editingId = row.id;
      this.employeePickerSearch = "";
      this.pdfFiles = [];
      this.existingPdfPath = row.pdf_path || row.pdfPath || null;
      this.uploadSizeError = "";
      let status = normalizeStatus(row.status);
      let nature = String(row.attrition_nature || "").toLowerCase();
      const raw = String(row.status || "").toLowerCase();
      if (!nature && ["deceased", "retired", "dismissed", "resigned"].includes(raw)) nature = raw;
      this.form = {
        record_number: row.record_number,
        employee_id: String(row.employee_id ?? row.employeeId ?? ""),
        status,
        attrition_nature: nature,
        notes: row.notes || "",
        transferred_to: row.transferred_to || "",
        status_date: row.status_date ? String(row.status_date).slice(0, 10) : new Date().toISOString().slice(0, 10)
      };
      this.formAudit = {
        created_by: row.created_by,
        created_at: row.created_at,
        updated_by: row.updated_by,
        updated_at: row.updated_at,
        file_uploaded_by: row.file_uploaded_by,
        file_uploaded_at: row.file_uploaded_at
      };
      // Ensure selected employee appears in the dropdown
      const selId = this.form.employee_id;
      const exists = (this.employees || []).some((e) => String(e.id) === String(selId));
      if (!exists && selId) {
        this.employees = [
          {
            id: selId,
            firstName: String(row.employee_name || "Employee").split(" ")[0] || "Employee",
            lastName: "",
            empNo: row.emp_no || row.empNo || "",
            departmentName: row.department_name || row.departmentName || ""
          },
          ...(this.employees || [])
        ];
      } else if (!this.employees.length) {
        this.loadEmployees("");
      }
      this.$refs.formModal?.open();
      this.$nextTick(() => this.resetPdfInput());
    },
    onPdfFilesChange(e) {
      const selected = Array.from(e.target.files || []);
      const maxBytes = this.maxUploadMb * 1024 * 1024;
      this.uploadSizeError = "";
      for (const f of selected) {
        if (f.size > maxBytes) {
          this.uploadSizeError = `"${f.name}" is larger than ${this.maxUploadMb}MB. Split or compress the file.`;
          this.resetPdfInput();
          return;
        }
      }
      this.pdfFiles = [...this.pdfFiles, ...selected];
    },
    formatFileSize(n) {
      const x = Number(n) || 0;
      if (x < 1024) return `${x} B`;
      if (x < 1048576) return `${(x / 1024).toFixed(1)} KB`;
      return `${(x / 1048576).toFixed(1)} MB`;
    },
    removeSelectedFile(index) {
      this.pdfFiles.splice(index, 1);
      this.uploadSizeError = "";
      this.resetPdfInput();
    },
    resetPdfInput() {
      const el = this.$refs.pdfFileInput;
      if (el) el.value = "";
    },
    getFilesList() {
      const path = this.existingPdfPath;
      if (!path) return [];
      try {
        const decoded = JSON.parse(path);
        if (Array.isArray(decoded)) return decoded.filter(Boolean);
      } catch (e) {
        /* single path */
      }
      return [path].filter(Boolean);
    },
    getFilesListFromRow(row) {
      const path = row.pdf_path || row.pdfPath || "";
      if (!path) return [];
      // Sometimes API double-encodes JSON array as a string
      let raw = path;
      if (typeof raw === "string" && raw.trim().startsWith("[")) {
        try {
          const decoded = JSON.parse(raw);
          if (Array.isArray(decoded)) return decoded.filter(Boolean);
        } catch (e) {
          /* continue */
        }
      }
      return [String(path)].filter(Boolean);
    },
    getFileName(path) {
      if (!path) return "file";
      const filename = path.split("/").pop();
      const matchNew = filename.match(/^fr_\d+_\d+_\d+_(.+)$/);
      if (matchNew) return matchNew[1];
      const matchIdx = filename.match(/^fr_\d+_\d+_(\d+)\.(.+)$/);
      if (matchIdx) return `File_${Number(matchIdx[1]) + 1}.${matchIdx[2]}`;
      const matchLegacy = filename.match(/^fr_\d+_\d+\.(.+)$/);
      if (matchLegacy) return `File.${matchLegacy[1]}`;
      return filename;
    },
    async openRegistryFileByIndex(index, rowId = null) {
      if (!this.canDownload) return;
      const id = rowId || this.editingId;
      if (!id) return;
      try {
        const data = new FormData();
        data.append("function", "downloadStaffFileRegistryPdf");
        data.append("id", String(id));
        data.append("file_index", String(index));
        const res = await executeDownload(data, authUrl);
        const blob = res.data;
        if (blob && blob.type && blob.type.indexOf("json") !== -1) {
          const text = await blob.text();
          try {
            const j = JSON.parse(text);
            window.alert(j.message || "Could not open file.");
          } catch (_) {
            window.alert("Could not open file.");
          }
          return;
        }
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 120000);
      } catch (err) {
        window.alert(err?.message || "Could not open file.");
      }
    },
    async downloadRegistryFileByIndex(index, filePath, row = null) {
      if (!this.canDownload) return;
      const id = row ? row.id : this.editingId;
      const recordNumber = row ? row.record_number : this.form.record_number;
      if (!id) return;
      try {
        const data = new FormData();
        data.append("function", "downloadStaffFileRegistryPdf");
        data.append("id", String(id));
        data.append("file_index", String(index));
        data.append("disposition", "attachment");
        const res = await executeDownload(data, authUrl);
        const blob = res.data;
        if (blob && blob.type && blob.type.indexOf("json") !== -1) {
          const text = await blob.text();
          try {
            const j = JSON.parse(text);
            window.alert(j.message || "Could not download file.");
          } catch (_) {
            window.alert("Could not download file.");
          }
          return;
        }
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        const pathStr = typeof filePath === "string" ? filePath : "";
        const ext = (pathStr.split(".").pop() || "pdf").toLowerCase();
        const baseName = (recordNumber || "record").replace(/[^A-Za-z0-9._-]/g, "_");
        a.href = url;
        a.download = `${baseName}_${index + 1}.${ext}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 120000);
      } catch (err) {
        window.alert(err?.message || "Could not download file.");
      }
    },
    async deleteRegistryFileByIndex(index) {
      if (!this.canDeleteFile || !this.editingId) return;
      if (!window.confirm("Delete this uploaded file?")) return;
      try {
        const data = new FormData();
        data.append("function", "deleteStaffFileRegistryPdf");
        data.append("id", String(this.editingId));
        data.append("file_index", String(index));
        const res = await execute(data, authUrl);
        if (res?.data?.success === false) {
          window.alert(res?.data?.message || "Could not delete file.");
          return;
        }
        const list = this.getFilesList();
        list.splice(index, 1);
        this.existingPdfPath = list.length === 0 ? null : JSON.stringify(list);
        await this.loadRecords();
      } catch (err) {
        window.alert(err?.message || "Could not delete file.");
      }
    },
    onStatusChange() {
      if (this.form.status !== "transferred_out") this.form.transferred_to = "";
      if (this.form.status !== "attrition") this.form.attrition_nature = "";
    },
    appendRegistryMeta(fd, recordId = null) {
      fd.append("function", "saveStaffFileRegistry");
      fd.append("record_number", this.form.record_number);
      fd.append("employee_id", this.form.employee_id);
      fd.append("status", this.form.status);
      fd.append("notes", this.form.notes || "");
      fd.append("transferred_to", this.form.transferred_to || "");
      fd.append("status_date", this.form.status_date || "");
      fd.append("attrition_nature", this.form.attrition_nature || "");
      const id = recordId != null ? recordId : this.editingId;
      if (id) fd.append("id", String(id));
    },
    attachFilesToFormData(fd, files) {
      (files || []).forEach((file) => {
        fd.append("pdfFiles", file, file.name || "upload.bin");
      });
      if ((files || []).length) fd.append("expectFiles", "1");
    },
    /** Read registry id from any known API envelope shape. */
    extractSavedRegistryId(res) {
      const body = res?.data;
      if (!body || typeof body !== "object") return null;
      const tryNum = (v) => {
        const n = Number(v);
        return Number.isFinite(n) && n > 0 ? n : null;
      };
      // Prefer nested success payloads used by compute::display
      const pools = [body.data, body, body.data?.data, body.Records, body.records];
      for (const p of pools) {
        if (!p || typeof p !== "object" || Array.isArray(p)) continue;
        for (const key of ["id", "registryId", "registry_id", "ID", "Id"]) {
          const n = tryNum(p[key]);
          if (n) return n;
        }
      }
      // Deep scan top-level for first positive id
      const walk = (obj, depth = 0) => {
        if (!obj || depth > 4) return null;
        if (typeof obj !== "object") return null;
        if (!Array.isArray(obj)) {
          for (const key of ["id", "registryId", "registry_id"]) {
            if (key in obj) {
              const n = tryNum(obj[key]);
              if (n) return n;
            }
          }
        }
        for (const v of Object.values(obj)) {
          if (v && typeof v === "object") {
            const found = walk(v, depth + 1);
            if (found) return found;
          }
        }
        return null;
      };
      return walk(body);
    },
    async saveRecord() {
      if (!this.form.record_number || !this.form.employee_id || !this.form.status) {
        window.alert("Record number, employee and category are required.");
        return;
      }
      if (this.form.status === "attrition" && !this.form.attrition_nature) {
        window.alert("Select nature of attrition (Deceased, Retired, Dismissed, or Resigned).");
        return;
      }
      if (this.uploadSizeError) return;
      this.saving = true;
      this.uploadProgress = 0;
      this.uploadProgressText = "";
      try {
        const files = Array.isArray(this.pdfFiles) ? [...this.pdfFiles] : [];
        let recordId = this.editingId || null;

        // Two-phase upload (reliable on UAT/prod reverse proxies):
        // 1) Save metadata without files → stable registry id
        // 2) Upload each file in its own request (smaller payloads, retries per file)
        this.uploadProgressText = "Saving record…";
        const metaRes = await executeUpload(
          () => {
            const fd = new FormData();
            this.appendRegistryMeta(fd, recordId);
            return fd;
          },
          authUrl,
          {
            timeout: 3 * 60 * 1000,
            retries: 2,
            retryDelayMs: 1500
          }
        );

        if (metaRes?.data?.success === false) {
          const next =
            metaRes?.data?.data?.nextRecordNumber ||
            metaRes?.data?.nextRecordNumber ||
            "";
          if (next) {
            this.serverNextRecordNumber = String(next).trim();
            this.form.record_number = this.serverNextRecordNumber;
          }
          window.alert(metaRes?.data?.message || "Save failed.");
          return;
        }

        recordId = this.extractSavedRegistryId(metaRes) || recordId;
        if (!recordId) {
          // Reload: unique number may have saved without returning id
          await this.loadRecords();
          const rn = String(this.form.record_number || "").trim().toUpperCase();
          const found = (this.records || []).find(
            (r) => String(r.record_number || "").trim().toUpperCase() === rn
          );
          recordId = found?.id || null;
        }
        if (!recordId) {
          window.alert(
            "Record may have been saved but the system could not read its ID. " +
              "Refresh the list; if the row appears, open Edit and upload the files again."
          );
          await this.loadRecords();
          return;
        }

        let attached = 0;
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          this.uploadProgressText = `Uploading file ${i + 1} of ${files.length}: ${file.name || "document"}…`;
          this.uploadProgress = Math.round((i / Math.max(files.length, 1)) * 100);
          const fileRes = await executeUpload(
            () => {
              const fd = new FormData();
              this.appendRegistryMeta(fd, recordId);
              this.attachFilesToFormData(fd, [file]);
              return fd;
            },
            authUrl,
            {
              timeout: 15 * 60 * 1000,
              retries: 3,
              retryDelayMs: 2500,
              onUploadProgress: (e) => {
                if (!e.total) return;
                const base = (i / Math.max(files.length, 1)) * 100;
                const slice = 100 / Math.max(files.length, 1);
                this.uploadProgress = Math.min(
                  99,
                  Math.round(base + (e.loaded / e.total) * slice)
                );
              }
            }
          );
          if (fileRes?.data?.success === false) {
            window.alert(
              fileRes?.data?.message ||
                `Failed uploading "${file.name || "file"}". Record is saved — open Edit and re-upload remaining files.`
            );
            await this.loadRecords();
            return;
          }
          const n = Number(
            fileRes?.data?.data?.filesAttached ?? fileRes?.data?.filesAttached ?? 0
          );
          attached += n > 0 ? n : 1;
        }

        if (files.length && attached === 0) {
          window.alert(
            "Record was saved but no file reached the server. " +
              "On UAT/production, ask ICT to set upload_max_filesize and post_max_size to at least 64M " +
              "(and nginx client_max_body_size if applicable). Then Edit the record and re-upload."
          );
          await this.loadRecords();
          return;
        }

        this.uploadProgress = 100;
        this.uploadProgressText = "Done.";
        this.pdfFiles = [];
        this.resetPdfInput();
        this.$refs.formModal?.close();
        await this.loadRecords();
      } catch (err) {
        console.error("Save error:", err);
        const code = err?.code || "";
        const msg = String(err?.message || "");
        let tip =
          "Could not complete save/upload. Check your internet connection and try again.";
        if (code === "ECONNABORTED" || /timeout/i.test(msg)) {
          tip =
            "Upload timed out. Try a smaller file, or ask ICT to raise PHP max_execution_time / proxy timeouts on UAT/production.";
        } else if (code === "ERR_NETWORK" || /network/i.test(msg)) {
          tip =
            "Network dropped mid-upload (common on UAT/production). Check the registry list — if the record exists without files, open Edit and re-upload. " +
            "Also ensure server upload_max_filesize / post_max_size and reverse-proxy body size limits are ≥ 64M.";
        } else if (msg) {
          tip = msg;
        }
        window.alert(tip);
        try {
          await this.loadRecords();
        } catch (_) {
          /* ignore */
        }
      } finally {
        this.saving = false;
        this.uploadProgress = 0;
        this.uploadProgressText = "";
      }
    },
    formatStatus(s) {
      const n = normalizeStatus(s);
      const found = STATUS_OPTIONS.find((o) => o.value === n);
      return found ? found.label : s || "—";
    },
    formatAttritionNature(row) {
      if (normalizeStatus(row.status) !== "attrition") return "—";
      let n = String(row.attrition_nature || "").toLowerCase();
      const raw = String(row.status || "").toLowerCase();
      if (!n && ["deceased", "retired", "dismissed", "resigned"].includes(raw)) n = raw;
      if (!n) return "—";
      return n.charAt(0).toUpperCase() + n.slice(1);
    },
    statusBadgeClass(s) {
      const t = normalizeStatus(s);
      if (t === "present_active") return "hr-badge-success";
      if (t === "missing_active") return "hr-badge-danger";
      if (t === "transferred_out") return "hr-badge-warning";
      if (t === "stoppage") return "hr-badge-secondary";
      if (t === "attrition") return "hr-badge-info";
      return "hr-badge-info";
    },
    formatDate(d) {
      if (!d) return "—";
      return String(d).slice(0, 10);
    },
    formatDateTime(d) {
      if (!d) return "—";
      const s = String(d).replace("T", " ");
      return s.length > 19 ? s.slice(0, 19) : s;
    },
    async generateReportPdf() {
      if (!this.canReport) return;
      this.generatingReport = true;
      try {
        if (!this.letterhead) await this.loadLetterhead();
        const logoDataUrl = await loadCountyLogoDataUrl(this.letterhead);
        const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
        const title = "STAFF FILE RECORDS MANAGEMENT REPORT";
        const startY = addCountyLetterhead(doc, {
          title,
          subtitle: "Record categories: Present Active · Missing Active · Stoppage · Transferred out · Attrition",
          periodLabel: new Date().toLocaleDateString("en-KE", {
            year: "numeric",
            month: "long",
            day: "numeric"
          }),
          logoDataUrl,
          letterhead: this.letterhead
        });

        const body = this.records.map((r, i) => [
          i + 1,
          r.record_number || "",
          r.employee_name || "",
          r.emp_no || "",
          r.department_name || "",
          this.formatStatus(r.status),
          this.formatAttritionNature(r),
          this.formatDate(r.status_date),
          r.created_by || "",
          this.formatDateTime(r.created_at),
          r.file_uploaded_by || "",
          this.formatDateTime(r.file_uploaded_at)
        ]);

        autoTable(doc, {
          startY,
          head: [[
            "#",
            "Record No.",
            "Employee",
            "Emp No",
            "Department",
            "Category",
            "Attrition nature",
            "Date document uploaded",
            "Created by",
            "Created when",
            "Upload by",
            "Upload when"
          ]],
          body,
          styles: { fontSize: 6.5, cellPadding: 1.2 },
          headStyles: { fillColor: [12, 74, 110], textColor: 255, fontStyle: "bold", fontSize: 6.5 },
          alternateRowStyles: { fillColor: [248, 250, 252] },
          margin: { left: 10, right: 10 }
        });

        const sum = this.reportSummary;
        let y = (doc.lastAutoTable && doc.lastAutoTable.finalY) || startY;
        y += 8;
        const pageH = doc.internal.pageSize.getHeight();
        if (y > pageH - 70) {
          doc.addPage();
          y = 18;
        }

        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.text("REPORT TOTALS", 14, y);
        y += 2;

        autoTable(doc, {
          startY: y + 2,
          head: [["Metric", "Count"]],
          body: [
            ["Total employees / records", String(sum.total)],
            ["Total Present Active records", String(sum.present_active)],
            ["Total Missing Active records", String(sum.missing_active)],
            ["Stoppage", String(sum.stoppage)],
            ["Transferred out", String(sum.transferred_out)],
            ["Attrition (all)", String(sum.attrition)],
            ["  — Deceased", String(sum.attrition_breakdown.deceased)],
            ["  — Retired", String(sum.attrition_breakdown.retired)],
            ["  — Dismissed", String(sum.attrition_breakdown.dismissed)],
            ["  — Resigned", String(sum.attrition_breakdown.resigned)]
          ],
          styles: { fontSize: 8 },
          headStyles: { fillColor: [12, 74, 110], textColor: 255 },
          columnStyles: { 0: { cellWidth: 80 }, 1: { cellWidth: 30, halign: "right" } },
          margin: { left: 14 }
        });

        y = (doc.lastAutoTable && doc.lastAutoTable.finalY) || y;
        y += 10;
        if (y > pageH - 50) {
          doc.addPage();
          y = 20;
        }

        const genDate = new Date().toLocaleString("en-KE");
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(71, 85, 105);
        doc.text(`Report generated: ${genDate}`, 14, y);
        y += 14;

        doc.setDrawColor(100, 116, 139);
        doc.setFontSize(9);
        doc.setTextColor(15, 23, 42);
        doc.text("Authorized signature: ________________________________", 14, y);
        y += 10;
        doc.text("Name / designation: ________________________________", 14, y);
        y += 10;
        doc.text("Date: ________________", 14, y);

        // Stamp box (right side)
        const pageW = doc.internal.pageSize.getWidth();
        doc.setDrawColor(12, 74, 110);
        doc.setLineWidth(0.4);
        doc.rect(pageW - 70, y - 34, 52, 40);
        doc.setFontSize(8);
        doc.setTextColor(100, 116, 139);
        doc.text("COUNTY STAMP", pageW - 44, y - 14, { align: "center" });
        doc.text("(affix here)", pageW - 44, y - 8, { align: "center" });

        // Footer on all pages
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i += 1) {
          doc.setPage(i);
          doc.setFontSize(7);
          doc.setTextColor(148, 163, 184);
          const footer =
            (this.letterhead && this.letterhead.reportFooter) ||
            "Official HRMIS document — County Government of Homa Bay";
          doc.text(footer, 14, pageH - 8);
          doc.text(`Page ${i} of ${pageCount}`, pageW - 14, pageH - 8, { align: "right" });
        }

        doc.save(`file_records_report_${new Date().toISOString().slice(0, 10)}.pdf`);
      } catch (e) {
        console.error(e);
        window.alert(e?.message || "Could not generate PDF report.");
      } finally {
        this.generatingReport = false;
      }
    }
  }
};
</script>

<style scoped>
.file-registry-content { max-width: 100%; }
.hr-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
.span-half { grid-column: span 1; }
.span-full { grid-column: 1 / -1; }
.hr-flex-col { display: flex; flex-direction: column; }
.hr-flex-between { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
.audit-box {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 12px;
}
.registry-file-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 4px 0;
}
.registry-file-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #f8fafc;
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}
.registry-file-info {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.registry-file-icon { font-size: 16px; color: #059669; flex-shrink: 0; }
.registry-file-name {
  font-size: 12px;
  font-weight: 500;
  color: #334155;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 130px;
}
.hr-btn.btn-compact {
  padding: 4px 8px;
  font-size: 11px;
  border-radius: 6px;
  gap: 4px;
}
.modal-file-list { display: flex; flex-direction: column; gap: 6px; }
.modal-file-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #f8fafc;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}
</style>
